import { defineStore } from 'pinia'
import type { CallStatus, CallState, VideoProviderType, SessionCredentials } from '@virtual-care/shared'

export interface ExtendedCallState extends CallState {
  provider?: VideoProviderType
  credentials?: SessionCredentials
  nurseName?: string
  callPurpose?: string
  /** Figma generic headline: "Your nurse is calling" */
  useGenericNurseLabel?: boolean
}

export const useCallStore = defineStore('call', {
  state: (): ExtendedCallState => ({
    sessionId: null,
    status: 'idle',
    roomId: null,
    initiatorId: null,
    isVideoEnabled: true,
    isAudioEnabled: true,
    connectionTime: undefined,
    error: undefined,
    provider: undefined,
    credentials: undefined,
    nurseName: undefined,
    callPurpose: undefined,
    useGenericNurseLabel: false,
  }),

  getters: {
    isInCall: (state): boolean => state.status === 'connected',
    isRinging: (state): boolean => state.status === 'ringing',
    isConnecting: (state): boolean => state.status === 'connecting',
    isMissed: (state): boolean => state.status === 'missed',
    isBreakthrough: (state): boolean => state.status === 'breakthrough',
    hasError: (state): boolean => state.error !== undefined,
    displayNurseName: (state): string => state.nurseName ?? 'Your Nurse',
  },

  actions: {
    setIncomingCall(
      sessionId: string,
      roomId: string,
      initiatorId: string,
      nurseName: string,
      callPurpose?: string,
      useGenericNurseLabel?: boolean
    ) {
      this.sessionId = sessionId
      this.roomId = roomId
      this.initiatorId = initiatorId
      this.nurseName = nurseName
      this.callPurpose = callPurpose
      this.useGenericNurseLabel = !!useGenericNurseLabel
      this.status = 'ringing'
    },

    setBreakthroughCall(sessionId: string, roomId: string, initiatorId: string, nurseName: string) {
      this.sessionId = sessionId
      this.roomId = roomId
      this.initiatorId = initiatorId
      this.nurseName = nurseName
      this.callPurpose = undefined
      this.useGenericNurseLabel = false
      this.status = 'breakthrough'
    },

    setMissedCall() {
      this.status = 'missed'
    },

    acceptCall(provider?: VideoProviderType, credentials?: SessionCredentials) {
      this.provider = provider
      this.credentials = credentials
      this.status = 'connecting'
    },

    declineCall() {
      this.$patch({
        sessionId: null,
        roomId: null,
        initiatorId: null,
        nurseName: undefined,
        callPurpose: undefined,
        useGenericNurseLabel: false,
        status: 'idle',
      })
    },

    setStatus(status: CallStatus) {
      this.status = status

      if (status === 'connected' && !this.connectionTime) {
        this.connectionTime = Date.now()
      }
    },

    toggleVideo() {
      this.isVideoEnabled = !this.isVideoEnabled
    },

    toggleAudio() {
      this.isAudioEnabled = !this.isAudioEnabled
    },

    setError(code: string, message: string, details?: unknown) {
      this.error = { code, message, details }
      this.status = 'failed'
    },

    clearError() {
      this.error = undefined
    },

    endCall() {
      this.$patch({
        sessionId: null,
        roomId: null,
        initiatorId: null,
        status: 'idle',
        connectionTime: undefined,
        error: undefined,
        provider: undefined,
        credentials: undefined,
        nurseName: undefined,
        callPurpose: undefined,
        useGenericNurseLabel: false,
      })
    },

    reset() {
      this.$reset()
    },
  },
})
